/**
 * @license
 * Copyright (c) 2004 %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

export type DateDef = {
  year: number;
  month: number; // 1-based!
  date: number;
  hours: number;
  minutes: number;
};
// cache the converters
const formatterCache: Map<string, Intl.DateTimeFormat> = new Map();

/**
 * Calculates the time zone offset (in minutes) between UTC and a specified time zone
 * for a given date, accounting for Daylight Saving Time (DST) transitions.
 * @param date - An object representing the date and time, e.g., date.year.
 * @param timeZone  - The IANA time zone name (e.g., "America/Los_Angeles")
 * @returns {number} The offset in minutes between UTC and the specified time zone,
 * accounting for potential Daylight Saving Time (DST) adjustments.
 */
export function getISODateOffset(date: DateDef, timeZone: string): number {
  // Special handling of first of the year with year < 100, e.g., 0001-01-01.
  if (date.year < 100 && date.month === 1 && date.date === 1) {
    // Increment date by 1 and run again so that we are not on a January 1 date, and instead on January 2 date. This
    // prevents the roll-back to the previous day staying on the same year bug for year < 100.
    // Instead we calculate the offset based on the day year-month-day+1 so that _applyTimezoneToDate doesn't encounter the issue
    // where when you are on January 1, you should get December 31 of the previous year, but for year < 100, you do not get the previous year
    // you get December 31 of the same year. The automatic rollback is not working for year < 100.
    // E.g., 0099-01-01 is the limit, subtract a date and you are getting the same year, like 0099-12-31. The expected is 0098-12-31.
    // So to calculate the offset, we use 0099-01-02 instead. Changing the day by one will not affect the offset for year < 100.
    // If we do not do this, then _getOffset gives a bogus offset, which then causes an Invalid Time error. This helps fix issues found in
    // JET-60050 OJ-INPUT-DATE COMPONENT UNABLE TO PARSE MIN-DATE AND MAX-DATE ATTRIBUTES FOR DATES < 100.
    const newDate = {
      year: date.year,
      month: date.month,
      date: date.date + 1,
      hours: date.hours,
      minutes: date.minutes
    };
    return getISODateOffset(newDate, timeZone);
  }

  // Get the timezone-adjusted representation of the date parameter interpreted as UTC
  const d = new Date(Date.UTC(date.year, date.month - 1, date.date, date.hours, date.minutes));
  if (date.year < 100) {
    d.setFullYear(date.year);
  }
  const utcDateAtTimezone = _applyTimezoneToDate(d, timeZone);
  // get the offset of the target timezone (subject to a possible Daylight Savings Time (DST) change adjustment)
  const offset = _getOffset(date, utcDateAtTimezone);
  let adjustment = 0;

  // check whether the offset is correct
  // no need to check for dst before year 100. Plus we have issues with year < 100 so don't do unnecessary work.
  if (!(date.year < 100)) {
    // shift the date at utc by the offset.
    d.setTime(d.getTime() - offset * 60000);

    const timeZonedDate1 = _applyTimezoneToDate(d, timeZone);
    // If the days and hours are the same then we know that the offset was correct and we do not have to adjust it.
    // If it is not the same, then we are on a Daylight Savings Time (DST) day and hour and we have to figure
    // out which way to adjust the offset.
    if (!_compareDates(timeZonedDate1, date)) {
      adjustment = -60;
      // try to accommodate 'fall back'/move 1 hour west, and see if the hour/day match then.
      d.setTime(d.getTime() + 60 * 60000);
      const timeZonedDate2 = _applyTimezoneToDate(d, timeZone);

      if (!_compareDates(timeZonedDate2, date)) {
        // Next, spring forward/move 1 hour east
        // This case should always produce a match unless we are hitting the missing hour during the 'spring forward'
        // JS Date() favors the DST in this case, so we will remain at the 'spring forward' offset even if _compareDates()
        // would have returned false.
        if (timeZone !== 'Asia/Beirut') {
          adjustment = 60;
        } else {
          // Fix for JET-75436 UNABLE TO SELECT SPECIFICALLY 31.03.2024 DATE FROM CALENDAR IN BEIRUT (EET) TIME ZONE
          // The 'offset' we calculate with const offset = _getOffset(date, utcDateAtTimezone) cannot be used with adjustment=0
          // for positive offsets. The formatted date will be off 1 hour.
          // And we cannot add 60 to it in 'spring forward'. The formatted date will be off 2 hours,
          // *behind* 2 hours of the date passed in. It should be 1 hour forward.
          // This fixes the bug where a timezone with a positive offset*
          // and has DST spring forward does not format to the correct date and time.
          // *Instead of fixing this for all offset >= 0, we decided to limit the scope to Asia/Beirut which is what
          // the customer complained about. Filed JET-76400 for DST issues with other timezones.
          adjustment = -60;
        }
      }
    }
  }

  const result = offset + adjustment;

  return result;
}

function _applyTimezoneToDate(d: Date, timeZone: string): DateDef {
  const cnv = _getConverter(timeZone);
  const formattedUTC = cnv.format(d);
  const [localDate, localTime] = formattedUTC.split(',');
  const [month, date, year] = localDate.split('/');
  const [hours, minutes] = localTime.trim().split(':');
  return {
    year: parseInt(year),
    month: parseInt(month),
    date: parseInt(date),
    hours: parseInt(hours),
    minutes: parseInt(minutes)
  };
}

function _getOffset(original: DateDef, asUTC: DateDef): number {
  let originalMins = original.hours * 60 + original.minutes;
  let utcMinutes = asUTC.hours * 60 + asUTC.minutes;
  // any shift in year, month or day indicates the following day
  let delta = original.year - asUTC.year;
  if (delta == 0) {
    delta = original.month - asUTC.month;
    if (delta === 0) {
      delta = original.date - asUTC.date;
    }
  }
  if (delta > 0) {
    originalMins += 24 * 60;
  } else if (delta < 0) {
    utcMinutes += 24 * 60;
  }
  return utcMinutes - originalMins;
}

function _compareDates(date1: DateDef, date2: DateDef) {
  return (
    date1.year === date2.year &&
    date1.month === date2.month &&
    date1.hours === date2.hours &&
    date1.minutes === date2.minutes
  );
}

function _getConverter(timezone: string) {
  let formatter = formatterCache.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
      timeZone: timezone
    });
    formatterCache.set(timezone, formatter);
  }
  return formatter;
}
